import { useQuery } from "@tanstack/react-query";
import { Ticket, UserPlus, UsersRound, type LucideIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { dashboardSummaryQuery } from "@/services/queries";
import { useTranslation } from "@/store/locale";

/**
 * The figures worth knowing before anything else.
 *
 * Wears the quick tiles' own language — the same tinted icon chip, the same
 * card, the same corner radius — because a dashboard where the top two rows
 * are visibly different systems is a dashboard nobody reads as one page.
 */
export function DashboardStats() {
  const t = useTranslation();
  const { data, isLoading } = useQuery(dashboardSummaryQuery());

  if (isLoading) {
    return (
      <div className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="rounded-card h-[104px]" />
        ))}
      </div>
    );
  }
  // Not worth a banner: a deployment that does not serve it, or a person who
  // may see none of it, simply gets the rest of the page.
  if (!data) return null;

  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {data.members ? (
        <Stat icon={UsersRound} label={t("dash.statMembers")} value={data.members.total} />
      ) : null}
      {data.non_members ? (
        <Stat
          icon={UserPlus}
          label={t("dash.statNonMembers")}
          value={data.non_members.total}
          hint={t("dash.statRecent").replace("{n}", String(data.non_members.last_30_days))}
        />
      ) : null}
      {data.promo_codes ? (
        <Stat
          icon={Ticket}
          label={t("dash.statPromoCodes")}
          value={data.promo_codes.active}
          hint={t("dash.statExpired").replace("{n}", String(data.promo_codes.expired))}
        />
      ) : null}
    </div>
  );
}

/**
 * How those members divide up: one card, three columns, nothing else.
 *
 * Status, tier and account type are three views of one number and sit side by
 * side rather than stacked, so the whole breakdown is one glance and one box.
 */
export function DashboardBreakdowns() {
  const t = useTranslation();
  const { data } = useQuery(dashboardSummaryQuery());
  if (!data?.members) return null;

  return (
    <section className="rounded-card bg-surface-raised mb-10 p-5">
      <h3 className="text-heading text-title-3">{t("dash.panelMembers")}</h3>
      <div className="mt-4 grid gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3"></div>
    </section>
  );
}

/** One headline figure. No plot, so no axes and nothing to hover. */
function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="rounded-card bg-surface-raised p-4">
      <span className="bg-accent-soft text-accent grid size-10 place-items-center rounded-[12px]">
        <Icon className="size-5" aria-hidden />
      </span>
      <p className="text-heading text-title-1 mt-3 tabular-nums">{value.toLocaleString()}</p>
      <p className="text-fg-muted text-subhead mt-0.5 font-medium">{label}</p>
      {/* `fg-subtle` clears 4.5:1 on white only by a hair; muted is the honest
          choice for twelve-point text nobody is meant to squint at. */}
      <p className="text-fg-muted text-footnote mt-1 min-h-4">{hint ?? ""}</p>
    </div>
  );
}
